import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Select from "react-select";
import { Users, UserPlus, Handshake, ChevronDown, ChevronRight, X } from "lucide-react";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import {
  listReferrals,
  referralsByReferrer,
  assignReferralCp,
  prepareInteriorSale,
  listChannelPartners,
} from "../../services/referralAdmin";

const TYPE_LABEL = { PROPERTY_PURCHASE: "New Home", INTERIORS: "Interiors" };
const TYPE_STYLE = {
  PROPERTY_PURCHASE: "bg-sky-50 text-sky-700 border-sky-200",
  INTERIORS: "bg-violet-50 text-violet-700 border-violet-200",
};
const ROUTING_LABEL = {
  AUTO_ASSIGNED: "Auto → CP",
  UNASSIGNED: "In bucket",
  MANUALLY_ASSIGNED: "Assigned",
};

// filter key → query params. Default "active" hides completed.
const FILTERS = [
  { key: "active", label: "Active", params: { converted: false } },
  { key: "property", label: "New Home", params: { converted: false, referralType: "PROPERTY_PURCHASE" } },
  { key: "interiors", label: "Interiors", params: { converted: false, referralType: "INTERIORS" } },
  { key: "bucket", label: "Bucket (assign CP)", params: { converted: false, routingStatus: "UNASSIGNED" } },
  { key: "completed", label: "Completed", params: { converted: true } },
];

const Referrals = () => {
  const [rows, setRows] = useState([]);
  const [byBuyer, setByBuyer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("active");
  const [showByBuyer, setShowByBuyer] = useState(false);
  const [assignFor, setAssignFor] = useState(null); // referral row
  const [cpOptions, setCpOptions] = useState([]);
  const [selectedCp, setSelectedCp] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = FILTERS.find((f) => f.key === filter)?.params || {};
      const [res, byRef] = await Promise.all([listReferrals(params), referralsByReferrer()]);
      setRows(Array.isArray(res) ? res : res?.items || []);
      setByBuyer(Array.isArray(byRef) ? byRef : []);
    } catch (e) {
      toast.error("Failed to load referrals");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const openAssign = async (r) => {
    setAssignFor(r);
    setSelectedCp(null);
    try {
      const cps = await listChannelPartners();
      setCpOptions((cps || []).map((c) => ({ value: c.customerId, label: c.name || `CP #${c.customerId}` })));
    } catch {
      setCpOptions([]);
    }
  };

  const doAssign = async () => {
    if (!selectedCp) return toast.error("Choose a channel partner");
    setBusy(true);
    try {
      await assignReferralCp(assignFor.id, selectedCp.value);
      toast.success("Assigned to channel partner (notified)");
      setAssignFor(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign");
    } finally {
      setBusy(false);
    }
  };

  const createSale = async (r) => {
    setBusy(true);
    try {
      await prepareInteriorSale(r.id);
      toast.success(
        "Lead onboarded to the BuildVizer project — create the sale for them from that project's Properties to grant app access."
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to prepare sale");
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      key: "referrer",
      header: "Referred by",
      width: 180,
      render: (r) => <span className="text-sm text-slate-700">{r.referrerName || `User #${r.referrerUserId}`}</span>,
    },
    {
      key: "referred",
      header: "Referred person",
      width: 220,
      render: (r) => (
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900 truncate">{r.referredName || "—"}</p>
          <p className="text-xs text-slate-500 truncate">
            {r.mobileNoWa || r.emailId || "—"}
            {r.referralType === "INTERIORS" &&
              [r.propertyUnitNo, r.propertyProjectName, r.propertyCity].filter(Boolean).length > 0
              ? ` · ${[r.propertyUnitNo, r.propertyProjectName, r.propertyCity].filter(Boolean).join(", ")}`
              : ""}
            {r.referralType === "PROPERTY_PURCHASE" && r.projectName ? ` · ${r.projectName}` : ""}
          </p>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      width: 110,
      render: (r) => (
        <span className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold border ${TYPE_STYLE[r.referralType]}`}>
          {TYPE_LABEL[r.referralType] || r.referralType}
        </span>
      ),
    },
    {
      key: "level",
      header: "Level",
      width: 70,
      align: "center",
      render: (r) => (r.referralType === "PROPERTY_PURCHASE" ? <span className="text-sm text-slate-700">L{r.referralLevel}</span> : "—"),
    },
    {
      key: "routing",
      header: "Routing / CP",
      width: 180,
      render: (r) => {
        if (r.referralType !== "PROPERTY_PURCHASE") return <span className="text-slate-400 text-sm">—</span>;
        return (
          <div>
            <p className="text-xs text-slate-500">{ROUTING_LABEL[r.routingStatus] || r.routingStatus}</p>
            {r.assignedAgentName ? <p className="text-sm text-slate-800">{r.assignedAgentName}</p> : null}
          </div>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      width: 110,
      render: (r) =>
        r.converted ? (
          <span className="inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200">
            Completed
          </span>
        ) : (
          <span className="inline-flex px-2 py-0.5 rounded-md text-[11px] font-semibold border bg-amber-50 text-amber-700 border-amber-200">
            Open
          </span>
        ),
    },
    {
      key: "actions",
      header: "Action",
      width: 150,
      align: "center",
      render: (r) => {
        if (r.converted) return <span className="text-slate-400 text-xs">Done</span>;
        if (r.referralType === "PROPERTY_PURCHASE" && r.routingStatus === "UNASSIGNED") {
          return (
            <button
              onClick={() => openAssign(r)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold border bg-white text-slate-600 border-slate-300 hover:bg-slate-50 disabled:opacity-50"
            >
              <Handshake size={13} /> Assign CP
            </button>
          );
        }
        if (r.referralType === "INTERIORS") {
          return (
            <button
              onClick={() => createSale(r)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold border bg-white text-slate-600 border-slate-300 hover:bg-slate-50 disabled:opacity-50"
            >
              <UserPlus size={13} /> Create sale
            </button>
          );
        }
        return <span className="text-slate-400 text-xs">—</span>;
      },
    },
  ];

  return (
    <div className="p-6 lg:p-8 flex flex-col gap-6 min-h-[calc(100vh-4rem)]">
      <PageHeader
        icon={Users}
        title="Referrals"
        subtitle="Buyer referrals for new homes and interiors — route, assign, and onboard."
      />

      {/* Per-buyer summary */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <button onClick={() => setShowByBuyer((v) => !v)} className="w-full flex items-center justify-between px-5 py-3 text-left">
          <span className="text-sm font-semibold text-slate-800">Referrals per buyer ({byBuyer.length})</span>
          {showByBuyer ? <ChevronDown size={18} className="text-slate-400" /> : <ChevronRight size={18} className="text-slate-400" />}
        </button>
        {showByBuyer && (
          <div className="px-5 pb-4">
            {byBuyer.length === 0 ? (
              <p className="text-sm text-slate-500">No referrals yet.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {byBuyer.map((b) => (
                  <div key={b.referrerUserId} className="flex items-center justify-between py-2">
                    <span className="text-sm text-slate-700">{b.referrerName || `User #${b.referrerUserId}`}</span>
                    <span className="text-xs text-slate-500">
                      {b.total} total · <span className="text-emerald-600">{b.converted} completed</span> · {b.pending} open
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${
              filter === f.key ? "bg-flamingo-600 text-white border-flamingo-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden p-5">
        <DataTable
          columns={columns}
          data={rows}
          loading={loading}
          minWidth={1000}
          rowKey={(r, i) => r?.id ?? i}
          emptyState={
            <div className="px-6 py-12 text-center">
              <Users size={28} className="mx-auto text-slate-300" />
              <p className="text-base font-semibold text-slate-900 mt-2">No referrals here</p>
              <p className="text-sm text-slate-500 mt-1">Buyer referrals appear here as they come in from the app.</p>
            </div>
          }
        />
      </div>

      {/* Assign CP modal */}
      {assignFor && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="text-base font-semibold text-slate-900">Assign to Channel Partner</h3>
              <button onClick={() => setAssignFor(null)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <div className="p-5">
              <p className="text-xs text-slate-500 mb-2">
                Level {assignFor.referralLevel} referral for {assignFor.referredName || "the lead"}. Choose a CP to hand it off.
              </p>
              <Select
                options={cpOptions}
                value={selectedCp}
                onChange={setSelectedCp}
                placeholder="Select a channel partner"
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </div>
            <div className="flex justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-200">
              <button onClick={() => setAssignFor(null)} className="rounded-xl border border-slate-300 text-slate-600 text-sm font-medium px-4 py-2">Cancel</button>
              <button onClick={doAssign} disabled={busy} className="rounded-xl bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2">Assign</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Referrals;
